import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client.js';
import { diagnosticoAPI, messageAPI } from '../services/api.js';
import { FEELINGS, FEELING_REASONS, FATIGUE_OPTIONS, CLOSURE_OPTIONS } from '../utils/constants';
import { showJornadaGuiaTrial } from '../data/jornadaConversationScript.js';
import '../components/JornadaConversation.css';
import { isWorkerRole } from '../utils/roles.js';
import { surveyDateAtNoon, toSurveyDateKey } from '../utils/surveyDate.js';
import { resolveDeliveryId } from '../utils/messageDelivery.js';
import BiometricScanner from '../components/BiometricScanner.jsx';
import WorkerEpaGuided from '../components/WorkerEpaGuided.jsx';
import QuizStepProgress from '../components/QuizStepProgress.jsx';
import './WorkerJornada.css';

/** Claves internas de llegada → check-in diario (API sin cambios). */
const JORNADA_LLEGADA = [
    { key: 'bien', emoji: '😊', label: 'Bien', feeling: 'bien', reason: null },
    { key: 'cansado', emoji: '😐', label: 'Cansado', feeling: 'regular', reason: null },
    { key: 'saturado', emoji: '😣', label: 'Saturado', feeling: 'mal', reason: 'bateria' },
    { key: 'tenso', emoji: '⚡', label: 'Tenso', feeling: 'mal', reason: 'tension' },
];

const JORNADA_CUERPO = [
    { value: 'estable', emoji: '🌊', label: 'Calma' },
    { value: 'tension', emoji: '⚡', label: 'Tensión' },
    { value: 'agotamiento', emoji: '😴', label: 'Agotamiento' },
];

const JORNADA_AYUDA = [
    { key: 'claridad', supportNeed: 'claridad', emoji: '🧭', label: 'Claridad' },
    { key: 'apoyo', supportNeed: 'conversacion', emoji: '🤝', label: 'Apoyo' },
    { key: 'pausa', supportNeed: 'pausa', emoji: '😴', label: 'Pausa' },
    { key: 'conversar', supportNeed: 'conversacion', emoji: '💬', label: 'Conversar' },
];

/** Dominios: mismos `value` que el API; textos cotidianos en pantalla. */
const JORNADA_DOMINIO = [
    { value: 'vitalidad', title: 'Energía y descanso', hint: 'Cuerpo, sueño, cansancio.' },
    { value: 'vinculos', title: 'Personas y clima', hint: 'Equipo, trato, comunicación.' },
    { value: 'nucleo_afectivo', title: 'Casa y afecto', hint: 'Familia, pareja, carga emocional.' },
    { value: 'estabilidad_economica', title: 'Dinero y tranquilidad', hint: 'Pagos, futuro, estabilidad.' },
];

const JORNADA_FRICCION = [
    { value: 'baja', label: 'Relativamente tranquila' },
    { value: 'alta', label: 'Muy cargada' },
    { value: 'relaciones', label: 'Sobre todo con personas' },
];

/** Motivos almacenados en historial (check-in) + los del formulario jornada. */
const ARRIVAL_REASON_LABELS = {
    ...FEELING_REASONS,
    no_dormi: { emoji: '😴', label: 'Descanso o sueño' },
    problemas_personales: { emoji: '🏠', label: 'Algo personal o familiar' },
    el_trabajo: { emoji: '⚙️', label: 'La carga del trabajo' },
    el_jefe: { emoji: '👤', label: 'Relación con la persona guía' },
};

function mapArrivalToEnergy(key) {
    if (key === 'bien') return 5;
    if (key === 'cansado') return 3;
    if (key === 'saturado') return 2;
    if (key === 'tenso') return 3;
    return 3;
}

function mapArrivalToFriction(key) {
    if (key === 'saturado' || key === 'tenso') return key === 'tenso' ? 'relaciones' : 'alta';
    return 'baja';
}

function frictionToneLabel(level) {
    if (level === 'red') return 'Hoy conviene acercarte a alguien de confianza en el trabajo.';
    if (level === 'yellow') return 'Hoy merece la pena cuidarte un poco más.';
    return 'Por ahora vas en buen equilibrio.';
}

function obsDoneStorageKey(userId) {
    const d = new Date().toISOString().split('T')[0];
    return `alinea_worker_obs_${userId || 'u'}_${d}`;
}

function obsInsightStorageKey(userId) {
    const d = new Date().toISOString().split('T')[0];
    return `alinea_worker_obs_insight_${userId || 'u'}_${d}`;
}

function somaticPauseStorageKey(userId) {
    const d = new Date().toISOString().split('T')[0];
    return `alinea_worker_somatic_${userId || 'u'}_${d}`;
}

function postCheckinStorageKey(userId) {
    const d = new Date().toISOString().split('T')[0];
    return `alinea_worker_postcheckin_${userId || 'u'}_${d}`;
}

function inferArrivalFromSurvey(startRow) {
    if (!startRow?.feeling) return null;
    const f = startRow.feeling;
    const r = startRow.feeling_reason;
    if (f === 'bien') return 'bien';
    if (f === 'regular') return 'cansado';
    if (f === 'mal') {
        if (r === 'no_dormi') return 'saturado';
        if (r === 'el_trabajo') return 'tenso';
        return 'saturado';
    }
    return null;
}

/** Últimos días con registro de llegada (una fila por fecha), orden cronológico. */
function lastMorningSummaries(rows, max = 5) {
    const byDate = new Map();
    for (const r of rows) {
        if (r.survey_type !== 'start' || !r.survey_date || !r.feeling) continue;
        const key = toSurveyDateKey(r.survey_date);
        if (!key) continue;
        if (!byDate.has(key)) byDate.set(key, r);
    }
    return [...byDate.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-max)
        .map(([date, row]) => ({ date, row }));
}

export default function WorkerJornada({ previewOperario = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdminPreview =
        previewOperario && (user?.rol === 'admin' || user?.rol === 'superadmin');
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [message, setMessage] = useState(null);
    const [step, setStep] = useState(1);
    const [fatigue, setFatigue] = useState(null);
    const [showThanks, setShowThanks] = useState(false);
    const [postSave, setPostSave] = useState({ status: 'idle', text: '' }); // idle | saving | success | error

    const [arrivalKey, setArrivalKey] = useState(null);
    const [bodySignal, setBodySignal] = useState('estable');
    const [ayudaKey, setAyudaKey] = useState('claridad');
    const [selectedDomain, setSelectedDomain] = useState('');
    const [obsEnergy, setObsEnergy] = useState(3);
    const [obsFriction, setObsFriction] = useState('baja');
    const [reflectionText, setReflectionText] = useState('');
    const [biometricData, setBiometricData] = useState(null);

    const [obsSubmitting, setObsSubmitting] = useState(false);
    const [obsError, setObsError] = useState('');
    const [obsInsight, setObsInsight] = useState(null);
    const [somaticPauseDone, setSomaticPauseDone] = useState(false);

    /** Detalle de un día tocado en «últimas llegadas» (modal, sin alert). */
    const [dayArrivalModal, setDayArrivalModal] = useState(null);
    const modalCloseRef = useRef(null);

    async function fetchDashboardData() {
        if (isAdminPreview) {
            setHistory([]);
            setMessage(null);
            setStep(1);
            setArrivalKey(null);
            setSomaticPauseDone(false);
            setObsInsight(null);
            setLoading(false);
            return;
        }
        try {
            const [histSettled, msgSettled] = await Promise.allSettled([
                diagnosticoAPI.getHistorial(),
                messageAPI.getHoy(),
            ]);

            let rows = [];
            if (histSettled.status === 'fulfilled') {
                rows = histSettled.value.data.data || [];
                setHistory(rows);
            } else {
                console.warn('Historial no disponible:', histSettled.reason);
            }

            if (msgSettled.status === 'fulfilled') {
                const m = msgSettled.value.data.data;
                setMessage(m ? { ...m, deliveryId: resolveDeliveryId(m) ?? m?.deliveryId } : null);
            } else {
                setMessage(null);
            }

            const hoy = new Date().toISOString().split('T')[0];
            const hoyRows = rows.filter((h) => toSurveyDateKey(h.survey_date) === hoy);
            const startHoy = hoyRows.find((h) => h.survey_type === 'start');
            const endHoy = hoyRows.find((h) => h.survey_type === 'end');

            if (!startHoy) setStep(1);
            else if (!endHoy) setStep(2);
            else setStep(3);

            if (startHoy) {
                setArrivalKey((prev) => prev ?? inferArrivalFromSurvey(startHoy));
            }

            const uid = user?.id;
            if (uid && sessionStorage.getItem(somaticPauseStorageKey(uid)) === '1') {
                setSomaticPauseDone(true);
            } else {
                setSomaticPauseDone(false);
            }

            if (uid && sessionStorage.getItem(obsDoneStorageKey(uid)) === '1') {
                const raw = sessionStorage.getItem(obsInsightStorageKey(uid));
                if (raw) {
                    try {
                        setObsInsight(JSON.parse(raw));
                    } catch {
                        setObsInsight({
                            fallback: true,
                            tone: 'Lectura guardada',
                            frictionLevel: 'green',
                            mainText:
                                'Tu lectura de hoy ya quedó guardada. Puedes afinarla de nuevo desde esta misma columna.',
                        });
                    }
                } else {
                    setObsInsight({
                        fallback: true,
                        tone: 'Lectura guardada',
                        frictionLevel: 'green',
                        mainText:
                            'Tu lectura de hoy ya quedó guardada. Puedes afinarla de nuevo desde esta misma columna.',
                    });
                }
            }
        } catch (err) {
            console.error('Error loading dashboard:', err);

            // Fallback: si acabamos de guardar un check-in pero el refetch falló,
            // evitamos que el usuario retroceda o vea un estado incongruente.
            const uid = user?.id;
            if (uid) {
                const raw = sessionStorage.getItem(postCheckinStorageKey(uid));
                if (raw) {
                    try {
                        const saved = JSON.parse(raw);
                        if (saved?.step) setStep(saved.step);
                    } catch {
                        /* ignore */
                    }
                }
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    useEffect(() => {
        if (arrivalKey) {
            setObsEnergy(mapArrivalToEnergy(arrivalKey));
            setObsFriction(mapArrivalToFriction(arrivalKey));
        }
    }, [arrivalKey]);

    useEffect(() => {
        if (!dayArrivalModal) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const t = window.setTimeout(() => modalCloseRef.current?.focus(), 50);
        const onKey = (e) => {
            if (e.key === 'Escape') setDayArrivalModal(null);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.clearTimeout(t);
            window.removeEventListener('keydown', onKey);
        };
    }, [dayArrivalModal]);

    /** Siempre antes de cualquier return: mismo orden de hooks en cada render. */
    const weekSummaries = useMemo(() => lastMorningSummaries(history, 5), [history]);

    const submitCheckin = async (tipo, estado_animo, causa_estado = null, nivel_fatiga = null) => {
        setPostSave({ status: 'saving', text: 'Guardando tu registro…' });
        try {
            await diagnosticoAPI.submitCheckin({
                tipo,
                estado_animo,
                causa_estado,
                fatiga: nivel_fatiga,
                ...(isAdminPreview ? { preview_only: true } : {}),
            });
            if (tipo === 'inicio') {
                setStep(2);
                if (user?.id) {
                    sessionStorage.setItem(
                        postCheckinStorageKey(user.id),
                        JSON.stringify({ step: 2, tipo: 'inicio', at: Date.now() })
                    );
                }
            } else if (tipo === 'fin') {
                setStep(3);
                if (user?.id) {
                    sessionStorage.setItem(
                        postCheckinStorageKey(user.id),
                        JSON.stringify({ step: 3, tipo: 'fin', at: Date.now() })
                    );
                }
            }
            setPostSave({
                status: 'success',
                text: isAdminPreview
                    ? 'Vista de prueba: no quedó registrado en reportes.'
                    : 'Listo. Registro guardado.',
            });
            window.setTimeout(() => setPostSave({ status: 'idle', text: '' }), 1600);
            if (!isAdminPreview) {
                try {
                    await fetchDashboardData();
                } catch {
                    /* fetchDashboardData ya maneja fallback */
                }
            }
        } catch (err) {
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Error desconocido';
            setPostSave({ status: 'error', text: `No se pudo guardar: ${msg}` });
            alert(`No se pudo guardar: ${msg}`);
            window.setTimeout(() => setPostSave({ status: 'idle', text: '' }), 2200);
        }
    };

    const onLlegada = async (opt) => {
        setArrivalKey(opt.key);
        if (opt.feeling === 'bien') {
            await submitCheckin('inicio', 'bien', null);
        } else {
            await submitCheckin('inicio', opt.feeling, opt.reason);
        }
    };

    const handleFin = async (feelingFin) => {
        await submitCheckin('fin', feelingFin, null, fatigue);
    };

    const handleReaction = async (feedback = 'useful') => {
        const deliveryId = resolveDeliveryId(message);
        if (deliveryId) {
            try {
                await messageAPI.submitFeedback(deliveryId, feedback);
            } catch (err) {
                console.warn('Feedback mensaje jornada:', err);
            }
        }
        setShowThanks(true);
        window.setTimeout(() => setShowThanks(false), 2600);
    };

    const acknowledgeSomaticPause = () => {
        if (user?.id) {
            sessionStorage.setItem(somaticPauseStorageKey(user.id), '1');
        }
        setSomaticPauseDone(true);
    };

    function resetObsSession() {
        if (user?.id) {
            sessionStorage.removeItem(obsDoneStorageKey(user.id));
            sessionStorage.removeItem(obsInsightStorageKey(user.id));
        }
        setObsInsight(null);
        setObsError('');
    }

    async function handleObservadorSubmit(e) {
        e.preventDefault();
        if (!arrivalKey) return;
        setObsSubmitting(true);
        setObsError('');
        try {
            const ayuda = JORNADA_AYUDA.find((a) => a.key === ayudaKey) || JORNADA_AYUDA[0];
            const payload = {
                energyLevel: Number(obsEnergy),
                bodySignal,
                workFriction: obsFriction,
                supportNeed: ayuda.supportNeed,
                selectedDomain: selectedDomain || '',
                reflectionText: reflectionText.trim(),
                ...(biometricData
                    ? {
                          biometric: {
                              bpm: biometricData.bpm,
                              ear: biometricData.ear,
                              tension: biometricData.tension,
                              feeling: biometricData.feeling,
                          },
                      }
                    : {}),
            };
            const res = await api.analyzeObservadorForm({
                ...payload,
                ...(isAdminPreview ? { preview_only: true } : {}),
            });
            if (!res.success || !res.data) {
                setObsError(res.error || 'No se pudo enviar.');
                return;
            }
            const d = res.data;
            const tone = frictionToneLabel(d.frictionLevel);
            const mainText = [d.leaderGuide?.question, d.leaderGuide?.action].filter(Boolean).join('\n\n');
            const epaLine =
                d.epaCapsule?.title && d.epaCapsule?.steps?.[0]
                    ? `${d.epaCapsule.title}: ${d.epaCapsule.steps[0]}`
                    : '';

            const companionBrief = typeof d.companionBrief === 'string' ? d.companionBrief.trim() : '';

            const insight = {
                tone,
                mainText: mainText || 'Gracias. Lo tomamos en cuenta.',
                epaLine,
                frictionLevel: d.frictionLevel,
                companionBrief,
            };
            setObsInsight(insight);
            setReflectionText('');
            if (user?.id && !isAdminPreview) {
                sessionStorage.setItem(obsDoneStorageKey(user.id), '1');
                sessionStorage.setItem(obsInsightStorageKey(user.id), JSON.stringify(insight));
            }
        } catch (err) {
            setObsError(err.message || 'No se pudo enviar.');
        } finally {
            setObsSubmitting(false);
        }
    }

    if (loading) {
        return <div className="wjo-loading">Un momento…</div>;
    }

    if (!isAdminPreview && isWorkerRole(user?.rol) && user?.needsRegistroManada === true) {
        return <Navigate to="/profile-quiz" replace />;
    }

    const firstName = user.nombre_completo?.split(' ')[0] || '';
    const uid = user?.id;
    const obsDone =
        !isAdminPreview && uid && sessionStorage.getItem(obsDoneStorageKey(uid)) === '1';
    /** Columna del observador siempre con formulario hasta enviar la lectura del día. */
    const showObsForm = !obsDone;
    const canSubmitObs = Boolean(arrivalKey && (step === 3 || (step === 2 && somaticPauseDone)));
    const obsBlockReason = !arrivalKey
        ? 'Elige primero cómo llegas hoy.'
        : step === 2 && !somaticPauseDone
          ? 'Completa la pausa somática para continuar.'
          : '';

    const stepStatus = isAdminPreview
        ? { label: 'Vista de prueba (opcional)', cls: 'wjo-status-mid' }
        : step === 3
          ? { label: 'Hoy registrado', cls: 'wjo-status-done' }
          : step === 2
            ? { label: 'Cierra tu jornada', cls: 'wjo-status-mid' }
            : { label: 'Registra tu llegada', cls: 'wjo-status-start' };

    return (
        <div className={`wjo${isAdminPreview ? ' wjo--preview' : ''}`} aria-label="Jornada">
            {isAdminPreview && (
                <div className="wjo-preview-banner" role="status">
                    <p>
                        <strong>Vista opcional del operario.</strong> Puedes probar llegada, cierre y lectura; no
                        queda pendiente en tu cuenta ni en los reportes del equipo.
                    </p>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/', { replace: true })}>
                        Volver al panel
                    </button>
                </div>
            )}
            {postSave.status !== 'idle' && (
                <div className={`wjo-postsave wjo-postsave-${postSave.status}`} role="status" aria-live="polite">
                    {postSave.text}
                </div>
            )}
            {!isAdminPreview && showJornadaGuiaTrial(user) ? (
                <div className="jguia-entry-banner">
                    <p>
                        <strong>Prueba nueva:</strong> jornada en conversación con el Guía. La pantalla de ahora no cambia.
                    </p>
                    <Link className="jguia-entry-btn" to="/jornada-guia">
                        Abrir Guía conversación
                    </Link>
                </div>
            ) : null}
            <div className="wjo-shell wjo-shell-3col">
                <div className="wjo-col wjo-col-jornada">
                    <div className="wjo-flow">
                        <QuizStepProgress step={step} totalSteps={3} />
                        <header className="wjo-block wjo-block-head">
                            <div className="wjo-head-row">
                                <div>
                                    <h1>Hola{firstName ? `, ${firstName}` : ''}.</h1>
                                    {user?.tenantName && (
                                        <p className="wjo-tenant-line">Empresa: {user.tenantName}</p>
                                    )}
                                </div>
                                <span className={`wjo-status ${stepStatus.cls}`}>{stepStatus.label}</span>
                            </div>
                        </header>

                        {step === 1 && (
                            <section className="wjo-block" aria-label="Cómo llegas hoy">
                                <p className="wjo-lead">¿Cómo llegas hoy?</p>
                                <p className="wjo-soft">Un toque basta.</p>
                                <div className="wjo-arrival">
                                    {JORNADA_LLEGADA.map((opt) => (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            className={`wjo-arrival-btn${arrivalKey === opt.key ? ' wjo-arrival-on' : ''}`}
                                            onClick={() => onLlegada(opt)}
                                        >
                                            <span className="wjo-arrival-emoji" aria-hidden>
                                                {opt.emoji}
                                            </span>
                                            <span className="wjo-arrival-label">{opt.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </section>
                        )}

                        {step >= 2 && arrivalKey && (
                            <section className="wjo-block wjo-block-arrival-done" aria-label="Llegada de hoy">
                                <p className="wjo-done">
                                    Llegada registrada
                                    {` · ${JORNADA_LLEGADA.find((o) => o.key === arrivalKey)?.label || ''}`}
                                </p>
                            </section>
                        )}

                        {step >= 2 && (
                            <>
                                {step === 2 && !somaticPauseDone && (
                                    <section className="wjo-block wjo-somatic" aria-label="Pausa somática">
                                        <p className="wjo-somatic-eyebrow">Pausa somática</p>
                                        <p className="wjo-lead">Un momento para el cuerpo</p>
                                        <p className="wjo-soft">
                                            Sin prisa: hombros abajo, mandíbula suelta, una respiración un poco más larga. Solo
                                            notar cómo estás, sin juzgar.
                                        </p>
                                        <p className="wjo-somatic-hint">
                                            Cuando quieras, sigue con el cierre del día. En el centro puedes registrar pausas EPA; a la
                                            derecha, la lectura amable del observador.
                                        </p>
                                        <button type="button" className="wjo-somatic-cta" onClick={acknowledgeSomaticPause}>
                                            Listo, sigo
                                        </button>
                                        <p className="wjo-somatic-after text-muted">
                                            Después de esta pausa podrás continuar con las siguientes preguntas de tu jornada.
                                        </p>
                                    </section>
                                )}

                                {((step === 2 && somaticPauseDone) || step === 3) && (
                                    <section className="wjo-block" aria-label="Cierre del día">
                                        {step === 2 && !fatigue && (
                                            <>
                                                <p className="wjo-lead">Al cerrar el día</p>
                                                <p className="wjo-soft">¿Cómo vas de cansancio?</p>
                                                <ul className="wjo-list">
                                                    {Object.entries(FATIGUE_OPTIONS).map(([key, item]) => (
                                                        <li key={key}>
                                                            <button type="button" className="wjo-pick" onClick={() => setFatigue(key)}>
                                                                <span className="wjo-pick-emoji" aria-hidden>
                                                                    {item.emoji}
                                                                </span>
                                                                {item.label}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        {step === 2 && fatigue && (
                                            <>
                                                <p className="wjo-lead">¿Te queda algo del trabajo dando vueltas?</p>
                                                <div className="wjo-inline-choices">
                                                    {Object.entries(CLOSURE_OPTIONS).map(([key, item]) => (
                                                        <button key={key} type="button" className="wjo-pick" onClick={() => handleFin(key)}>
                                                            <span className="wjo-pick-emoji" aria-hidden>
                                                                {item.emoji}
                                                            </span>
                                                            {item.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {step === 3 && (
                                            <p className="wjo-done">Listo por hoy. Gracias por registrar cómo te fue.</p>
                                        )}
                                    </section>
                                )}
                            </>
                        )}

                        {message?.content && (
                            <section className="wjo-block" aria-label="Mensaje para ti">
                                <p className="wjo-lead">Un mensaje para ti</p>
                                <p className="wjo-msg">{message.content}</p>
                                <div className="wjo-reacts">
                                    <button type="button" className="wjo-react" onClick={handleReaction}>
                                        Me sirve
                                    </button>
                                    <button type="button" className="wjo-react" onClick={handleReaction}>
                                        Gracias
                                    </button>
                                    <button type="button" className="wjo-react" onClick={handleReaction}>
                                        Lo anoto
                                    </button>
                                </div>
                                {showThanks && <p className="wjo-thanks">Gracias por responder.</p>}
                            </section>
                        )}

                        {weekSummaries.length > 0 && (
                            <section className="wjo-block" aria-label="Últimos días con registro de llegada">
                                <p className="wjo-lead">Tus últimas llegadas</p>
                                <p className="wjo-soft">
                                    Últimos 5 días con registro, del más antiguo al más reciente. Toca un día para ver el detalle.
                                </p>
                                <div className="wjo-week-chips" role="list">
                                    {weekSummaries.map(({ date, row }) => {
                                        const dateObj = surveyDateAtNoon(date);
                                        const dayShort = dateObj
                                            ? dateObj.toLocaleDateString('es-ES', {
                                                  weekday: 'short',
                                                  day: 'numeric',
                                                  month: 'short',
                                              })
                                            : '—';
                                        const dateLong = dateObj
                                            ? dateObj.toLocaleDateString('es-ES', {
                                                  weekday: 'long',
                                                  day: 'numeric',
                                                  month: 'long',
                                              })
                                            : 'Fecha no disponible';
                                        const em = FEELINGS[row.feeling]?.emoji || '·';
                                        const feelingLabel = FEELINGS[row.feeling]?.label || row.feeling;
                                        return (
                                            <button
                                                key={date}
                                                type="button"
                                                className="wjo-week-chip"
                                                title={`${dateLong}: ${feelingLabel}`}
                                                aria-label={`Ver detalle: ${dateLong}, ${feelingLabel}`}
                                                onClick={() =>
                                                    setDayArrivalModal({
                                                        dateIso: date,
                                                        dateLong,
                                                        feelingLabel,
                                                        emoji: em,
                                                        feelingReason: row.feeling_reason || null,
                                                    })
                                                }
                                            >
                                                <span className="wjo-week-chip-emoji" aria-hidden>
                                                    {em}
                                                </span>
                                                <span className="wjo-week-chip-day">{dayShort}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        <footer className="wjo-tail">
                            <Link to="/survey" className="wjo-tool-link">
                                <span aria-hidden>📋</span> Otro registro o encuesta{' '}
                                <span className="wjo-tool-opt">(opcional)</span>
                            </Link>
                        </footer>
                    </div>
                </div>

                <div className="wjo-col wjo-col-epa" aria-label="Pausas y recursos EPA">
                    <div className="wjo-epa-col-inner">
                        <WorkerEpaGuided />
                    </div>
                </div>

                <aside className="wjo-col wjo-col-obs" aria-label="Observador en tu jornada">
                    <div className="wjo-side-inner">
                        <p className="wjo-side-eyebrow">Observador</p>
                        <h2 className="wjo-side-title">Tu pausa consciente</h2>
                        <p className="wjo-side-lead">
                            Afinas cómo te sientes hoy en la misma pantalla de tu jornada, sin cambiar de sitio.
                        </p>

                        {step === 1 && !arrivalKey && (
                            <p className="wjo-side-hint wjo-side-hint-spaced">
                                Elige primero «cómo llegas» en la primera columna; luego podrás enviar la lectura amable aquí.
                            </p>
                        )}

                        {step === 2 && !somaticPauseDone && arrivalKey && (
                            <p className="wjo-side-hint wjo-side-hint-spaced">
                                Cuando pulses «Listo, sigo» en la pausa somática, podrás enviar la lectura aquí.
                            </p>
                        )}

                        {showObsForm && (
                            <form className="wjo-side-form" onSubmit={handleObservadorSubmit}>
                                <div className="wjo-bio-block">
                                    <p className="wjo-side-section">Opcional: cámara en tu equipo</p>
                                    <p className="wjo-bio-lead">
                                        Opcional: calibración numérica en tu equipo. No guardamos vídeo ni mostramos lecturas
                                        sobre tu estado; solo índices para sincronizar la jornada.
                                    </p>
                                    <div className="wjo-bio-scanner">
                                        <BiometricScanner
                                            metricsOnly
                                            onDataCaptured={(data) => setBiometricData(data || null)}
                                        />
                                    </div>
                                    {biometricData ? (
                                        <p className="wjo-side-hint">
                                            Índices listos en tu equipo (sin vídeo). Se usan solo si el Estado
                                            Funcional está activo.
                                        </p>
                                    ) : null}
                                </div>

                                <p className="wjo-side-section">Tu cuerpo ahora</p>
                                <div className="wjo-chip-row wjo-chip-row-tight">
                                    {JORNADA_CUERPO.map((c) => (
                                        <button
                                            key={c.value}
                                            type="button"
                                            className={`wjo-chip${bodySignal === c.value ? ' wjo-chip-on' : ''}`}
                                            onClick={() => {
                                                setBodySignal(c.value);
                                                setObsError('');
                                            }}
                                        >
                                            <span aria-hidden>{c.emoji}</span> {c.label}
                                        </button>
                                    ))}
                                </div>

                                <p className="wjo-side-section">¿Qué te ayudaría más?</p>
                                <div className="wjo-chip-row wjo-chip-row-tight">
                                    {JORNADA_AYUDA.map((a) => (
                                        <button
                                            key={a.key}
                                            type="button"
                                            className={`wjo-chip${ayudaKey === a.key ? ' wjo-chip-on' : ''}`}
                                            onClick={() => {
                                                setAyudaKey(a.key);
                                                setObsError('');
                                            }}
                                        >
                                            <span aria-hidden>{a.emoji}</span> {a.label}
                                        </button>
                                    ))}
                                </div>

                                <p className="wjo-side-section">¿Dónde sientes más peso hoy?</p>
                                <div className="wjo-domain-grid">
                                    {JORNADA_DOMINIO.map((d) => (
                                        <button
                                            key={d.value}
                                            type="button"
                                            className={`wjo-domain-tile${selectedDomain === d.value ? ' wjo-domain-tile-on' : ''}`}
                                            onClick={() => {
                                                setSelectedDomain(d.value);
                                                setObsError('');
                                            }}
                                        >
                                            <span className="wjo-domain-title">{d.title}</span>
                                            <span className="wjo-domain-hint">{d.hint}</span>
                                        </button>
                                    ))}
                                </div>

                                <p className="wjo-side-section">Carga del trabajo (ajusta si hace falta)</p>
                                <div className="wjo-chip-row wjo-chip-row-tight">
                                    {JORNADA_FRICCION.map((f) => (
                                        <button
                                            key={f.value}
                                            type="button"
                                            className={`wjo-chip${obsFriction === f.value ? ' wjo-chip-on' : ''}`}
                                            onClick={() => {
                                                setObsFriction(f.value);
                                                setObsError('');
                                            }}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>

                                <label className="wjo-side-section wjo-side-label" htmlFor="wjo-obs-energy">
                                    Energía hoy (1 muy baja — 5 muy alta)
                                </label>
                                <div className="wjo-energy-row">
                                    <input
                                        id="wjo-obs-energy"
                                        type="range"
                                        min={1}
                                        max={5}
                                        value={obsEnergy}
                                        onChange={(e) => setObsEnergy(Number(e.target.value))}
                                        className="wjo-energy-range"
                                    />
                                    <span className="wjo-energy-val">{obsEnergy}</span>
                                </div>

                                <label className="wjo-side-section wjo-side-label" htmlFor="wjo-obs-reflect">
                                    Si quieres, una frase tuya…
                                </label>
                                <textarea
                                    id="wjo-obs-reflect"
                                    className="wjo-side-textarea"
                                    rows={3}
                                    value={reflectionText}
                                    onChange={(e) => setReflectionText(e.target.value)}
                                    placeholder="Opcional. Con tus palabras."
                                    maxLength={2000}
                                    autoComplete="off"
                                />

                                {obsError ? <p className="wjo-obs-err">{obsError}</p> : null}

                                <button type="submit" className="wjo-side-submit" disabled={obsSubmitting || !canSubmitObs}>
                                    {obsSubmitting ? 'Enviando…' : 'Ver lectura amable'}
                                </button>
                                {!canSubmitObs && obsBlockReason ? (
                                    <p className="wjo-side-hint" style={{ marginTop: '0.6rem' }}>
                                        {obsBlockReason}
                                    </p>
                                ) : null}
                            </form>
                        )}

                        {obsDone && obsInsight && (
                            <div className="wjo-side-result">
                                <h3 className="wjo-side-reading-title">Lectura de hoy</h3>
                                {obsInsight.companionBrief ? (
                                    <p className="wjo-side-companion">{obsInsight.companionBrief}</p>
                                ) : null}
                                <p
                                    className={`wjo-tone ${
                                        obsInsight.frictionLevel
                                            ? `wjo-tone-${obsInsight.frictionLevel}`
                                            : 'wjo-tone-green'
                                    }`}
                                >
                                    {obsInsight.tone}
                                </p>
                                {obsInsight.mainText ? (
                                    <p className="wjo-side-result-text">{obsInsight.mainText}</p>
                                ) : null}
                                {obsInsight.epaLine ? <p className="wjo-side-epa">{obsInsight.epaLine}</p> : null}
                                <button type="button" className="wjo-side-secondary" onClick={resetObsSession}>
                                    Afinar de nuevo
                                </button>
                            </div>
                        )}

                        {obsDone && !obsInsight && (
                            <p className="wjo-side-hint">
                                Lectura guardada. Puedes seguir con tu día o usar «Afinar de nuevo» si aparece arriba.
                            </p>
                        )}
                    </div>
                </aside>
            </div>

            {dayArrivalModal ? (
                <div
                    className="wjo-modal-backdrop"
                    role="presentation"
                    onClick={() => setDayArrivalModal(null)}
                >
                    <div
                        className="wjo-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="wjo-modal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            ref={modalCloseRef}
                            type="button"
                            className="wjo-modal-close"
                            aria-label="Cerrar"
                            onClick={() => setDayArrivalModal(null)}
                        >
                            Cerrar
                        </button>
                        <p className="wjo-modal-kicker">Registro de llegada</p>
                        <h2 id="wjo-modal-title" className="wjo-modal-title">
                            {dayArrivalModal.dateLong}
                        </h2>
                        <p className="wjo-modal-meta">Fecha: {dayArrivalModal.dateIso}</p>
                        <div className="wjo-modal-body">
                            <span className="wjo-modal-emoji" aria-hidden>
                                {dayArrivalModal.emoji}
                            </span>
                            <div>
                                <p className="wjo-modal-label">Cómo registraste la llegada</p>
                                <p className="wjo-modal-value">{dayArrivalModal.feelingLabel}</p>
                                {dayArrivalModal.feelingReason && ARRIVAL_REASON_LABELS[dayArrivalModal.feelingReason] ? (
                                    <p className="wjo-modal-note">
                                        {ARRIVAL_REASON_LABELS[dayArrivalModal.feelingReason].emoji}{' '}
                                        {ARRIVAL_REASON_LABELS[dayArrivalModal.feelingReason].label}
                                    </p>
                                ) : dayArrivalModal.feelingReason ? (
                                    <p className="wjo-modal-note">Detalle: {dayArrivalModal.feelingReason}</p>
                                ) : null}
                            </div>
                        </div>
                        <p className="wjo-modal-foot">Solo lectura de tu historial; no cambia nada al tocar aquí.</p>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
